import { useEffect, useState } from "react";
import { AppChrome } from "@/components/Chrome";
import { TruncatedAddr, SOLSCAN } from "@/components/Primitives";
import { Hallmark } from "@/components/Sigil";
import { useAuth } from "@/components/providers/AuthProvider";
import { useTheme } from "@/hooks/useTheme";
import { api } from "@/lib/api";

type SaveState = "idle" | "saving" | "saved" | "error";

export function SettingsPage() {
  const { operator, token, setOperator } = useAuth();
  const [displayName, setDisplayName] = useState(operator?.display_name ?? "");
  const [email, setEmail] = useState(operator?.email ?? "");
  const [slackUrl, setSlackUrl] = useState(operator?.alert_slack_webhook ?? "");
  const [profileSave, setProfileSave] = useState<SaveState>("idle");
  const [alertsSave, setAlertsSave] = useState<SaveState>("idle");
  const [profileErr, setProfileErr] = useState<string | null>(null);
  const [alertsErr, setAlertsErr] = useState<string | null>(null);
  const [theme, setTheme] = useTheme();

  useEffect(() => {
    setDisplayName(operator?.display_name ?? "");
    setEmail(operator?.email ?? "");
    setSlackUrl(operator?.alert_slack_webhook ?? "");
  }, [operator?.display_name, operator?.email, operator?.alert_slack_webhook]);

  const saveProfile = async () => {
    if (!token) return;
    setProfileSave("saving");
    setProfileErr(null);
    try {
      const updated = await api.updateOperatorMe(
        {
          display_name: displayName.trim() === "" ? null : displayName.trim(),
          email: email.trim() === "" ? null : email.trim(),
        },
        token,
      );
      setOperator(updated);
      setProfileSave("saved");
      setTimeout(() => setProfileSave("idle"), 2000);
    } catch (e) {
      setProfileSave("error");
      setProfileErr(e instanceof Error ? e.message : "save failed");
    }
  };

  const saveAlerts = async () => {
    if (!token) return;
    setAlertsSave("saving");
    setAlertsErr(null);
    try {
      const updated = await api.updateOperatorMe(
        { alert_slack_webhook: slackUrl.trim() === "" ? null : slackUrl.trim() },
        token,
      );
      setOperator(updated);
      setAlertsSave("saved");
      setTimeout(() => setAlertsSave("idle"), 2000);
    } catch (e) {
      setAlertsSave("error");
      setAlertsErr(e instanceof Error ? e.message : "save failed");
    }
  };

  return (
    <AppChrome>
      <div className="page page--settings">
        <div className="page__head">
          <div>
            <span className="kicker">Operator · org</span>
            <h1 className="page__title">Settings</h1>
          </div>
        </div>

        <div className="settings-grid">
          {/* Profile */}
          <section className="settings-section">
            <h2 className="settings__section-title">Profile</h2>
            <p className="settings__section-sub">
              How you're identified in the chronicle. Your wallet is the source of truth — display name + email are convenience.
            </p>

            <div className="settings__rows">
              <SettingsRow label="Wallet" readOnly>
                {operator && (
                  <TruncatedAddr addr={operator.wallet_pubkey} href={SOLSCAN(operator.wallet_pubkey)} />
                )}
              </SettingsRow>
              <SettingsRow label="Display name">
                <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="orange-syndicate" />
              </SettingsRow>
              <SettingsRow label="Alert email">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ops@yourprotocol.xyz"
                />
              </SettingsRow>
              <SettingsRow label="Plan tier" readOnly>
                <span className="settings__pill">{operator?.plan_tier ?? "free"}</span>
              </SettingsRow>
            </div>

            <div className="settings__actions">
              <button
                className="btn btn--primary"
                onClick={saveProfile}
                disabled={profileSave === "saving"}
              >
                {profileSave === "saving" ? "Saving…" : profileSave === "saved" ? "Saved ✓" : "Save profile"}
              </button>
              {profileErr && <span className="settings__err">{profileErr}</span>}
            </div>
          </section>

          {/* Alerts */}
          <section className="settings-section">
            <h2 className="settings__section-title">Alerts</h2>
            <p className="settings__section-sub">
              Where to send the kicker when something denies, breaks, or flags. Slack and email both work; both fire on the same trigger.
            </p>

            <div className="settings__rows">
              <SettingsRow label="Slack webhook">
                <input
                  type="url"
                  value={slackUrl}
                  onChange={(e) => setSlackUrl(e.target.value)}
                  placeholder="https://hooks.slack.com/services/…"
                />
              </SettingsRow>
              <SettingsRow label="Trigger" readOnly>
                <ul className="settings__list">
                  <li>· Any denial event (over_per_tx_cap, scope_violation_on_chain)</li>
                  <li>· Any session_key_revoked event</li>
                  <li>· Audit reconciliation gap &gt; 5 events / hour</li>
                </ul>
              </SettingsRow>
            </div>

            <div className="settings__actions">
              <button
                className="btn btn--primary"
                onClick={saveAlerts}
                disabled={alertsSave === "saving"}
              >
                {alertsSave === "saving" ? "Saving…" : alertsSave === "saved" ? "Saved ✓" : "Save alerts"}
              </button>
              {alertsErr && <span className="settings__err">{alertsErr}</span>}
            </div>
          </section>

          {/* Theme */}
          <section className="settings-section">
            <h2 className="settings__section-title">Appearance</h2>
            <p className="settings__section-sub">
              Light is default — parchment + warm ink. Dark inverts luminance for low-light ops.
            </p>
            <div className="theme-toggle">
              <button
                className={`theme-option ${theme === "light" ? "theme-option--active" : ""}`}
                onClick={() => setTheme("light")}
              >
                <span className="kicker">Light</span>
                <div className="theme-swatch theme-swatch--light" />
              </button>
              <button
                className={`theme-option ${theme === "dark" ? "theme-option--active" : ""}`}
                onClick={() => setTheme("dark")}
              >
                <span className="kicker">Dark</span>
                <div className="theme-swatch theme-swatch--dark" />
              </button>
            </div>
            <p className="settings__section-sub settings__section-sub--hint">
              Quick toggle is in the top-right of the navbar.
            </p>
          </section>

          {/* On-chain references */}
          <section className="settings-section">
            <h2 className="settings__section-title">On-chain references</h2>
            <p className="settings__section-sub">
              The deployed program your agents register against. Devnet today. Mainnet on the roadmap.
            </p>

            <div className="settings__refs">
              <div className="settings__ref-row">
                <Hallmark size={11} tone="bronze" />
                <span className="kicker">Identity Program</span>
                <TruncatedAddr addr="AkKx54ZmuP17r1sXsKr7mxe3dXJ5RMqsSH2zf8QGZ39C" href="https://solscan.io/account/AkKx54ZmuP17r1sXsKr7mxe3dXJ5RMqsSH2zf8QGZ39C?cluster=devnet" />
              </div>
              <div className="settings__ref-row">
                <Hallmark size={11} tone="bronze" />
                <span className="kicker">Genesis tx</span>
                <TruncatedAddr addr="57jLw4fMTbMsaxxKxjxfPAQe34PE7oQZRFZnun5RsabZjbLLS3xwaW4yjL8qD2H7cj8UDMy3mayJTuKzhuMYvKHm" href="https://solscan.io/tx/57jLw4fMTbMsaxxKxjxfPAQe34PE7oQZRFZnun5RsabZjbLLS3xwaW4yjL8qD2H7cj8UDMy3mayJTuKzhuMYvKHm?cluster=devnet" />
              </div>
              <div className="settings__ref-row">
                <Hallmark size={11} tone="bronze" />
                <span className="kicker">Swig substrate</span>
                <TruncatedAddr addr="swigypWHEksbC64pWKwah1WTeh9JXwx8H1rJHLdbQMB" href="https://solscan.io/account/swigypWHEksbC64pWKwah1WTeh9JXwx8H1rJHLdbQMB?cluster=devnet" />
              </div>
            </div>
          </section>

          {/* Danger zone */}
          <section className="settings-section danger-zone">
            <h2 className="settings__section-title danger-zone__title">⊗ Danger zone</h2>
            <p className="settings__section-sub">
              These actions are <strong>irreversible</strong>. Each requires a final confirmation.
            </p>

            <div className="danger-zone__action">
              <div>
                <h3 className="danger-zone__action-title">Revoke all agents</h3>
                <p className="danger-zone__action-body">
                  Crack every seal at once. Every active agent's session key is removed from Swig in a single batched transaction. Fleet goes quiet.
                </p>
              </div>
              <button
                className="btn btn--destructive"
                disabled
                title="Coming soon — for now, revoke each agent individually from its detail page."
              >
                Coming soon
              </button>
            </div>

            <div className="danger-zone__action">
              <div>
                <h3 className="danger-zone__action-title">Delete operator account</h3>
                <p className="danger-zone__action-body">
                  Removes the operator row from Altheia. On-chain agents survive (they're inscribed) but lose dashboard access. Your wallet remains.
                </p>
              </div>
              <button
                className="btn btn--destructive"
                disabled
                title="Coming soon — contact support to delete the operator row."
              >
                Coming soon
              </button>
            </div>
          </section>
        </div>

        <SettingsStyles />
      </div>
    </AppChrome>
  );
}

function SettingsRow({
  label,
  children,
  readOnly,
}: {
  label: string;
  children: React.ReactNode;
  readOnly?: boolean;
}) {
  return (
    <div className={`settings-row ${readOnly ? "settings-row--readonly" : ""}`}>
      <span className="kicker settings-row__label">{label}</span>
      <div className="settings-row__field">{children}</div>
    </div>
  );
}

function SettingsStyles() {
  return (
    <style>{`
      .settings-grid { display: flex; flex-direction: column; gap: var(--s-7); }
      .settings-section { background: var(--bg-elev); border: 1px solid var(--line-soft); padding: var(--s-5) var(--s-6); }
      .settings__section-title { font: var(--type-display-section); margin: 0 0 var(--s-2); }
      .settings__section-sub { color: var(--slate); font: var(--type-body); margin-bottom: var(--s-5); max-width: 640px; }
      .settings__section-sub--hint { margin-top: var(--s-4); margin-bottom: 0; color: var(--slate-2); font-size: 12px; }
      .settings__rows { display: flex; flex-direction: column; gap: var(--s-3); }
      .settings-row { display: grid; grid-template-columns: 180px 1fr; gap: var(--s-4); align-items: baseline; padding-bottom: var(--s-3); border-bottom: 1px dashed var(--line-soft); }
      .settings-row:last-child { border-bottom: 0; }
      .settings-row__label { padding-top: var(--s-2); }
      .settings-row__field input { width: 100%; max-width: 480px; background: var(--bg); border: 1px solid var(--line); padding: var(--s-2) var(--s-3); font: var(--type-body); color: var(--bone); }
      .settings-row__field input:focus { outline: none; border-color: var(--bronze); }
      .settings__pill { display: inline-block; padding: 2px 8px; border: 1px solid var(--line); font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--bronze); background: var(--bronze-tint); }
      .settings__actions { display: flex; gap: var(--s-3); margin-top: var(--s-5); padding-top: var(--s-5); border-top: 1px solid var(--line-soft); align-items: center; }
      .settings__err { color: var(--oxblood); font: var(--type-body-small); }
      .btn:disabled { opacity: 0.6; cursor: not-allowed; }
      .settings__list { list-style: none; padding: 0; margin: 0; font: var(--type-body-small); color: var(--slate); display: flex; flex-direction: column; gap: var(--s-1); }
      .theme-toggle { display: flex; gap: var(--s-3); }
      .theme-option { display: flex; flex-direction: column; gap: var(--s-2); padding: var(--s-3); border: 1px solid var(--line); align-items: center; min-width: 120px; }
      .theme-option--active { border-color: var(--bronze); background: var(--bronze-tint); }
      .theme-swatch { width: 80px; height: 60px; border: 1px solid var(--line); }
      .theme-swatch--light { background: linear-gradient(180deg, #F8F4EA 0%, #FCFAF4 100%); }
      .theme-swatch--dark { background: linear-gradient(180deg, #0D0B08 0%, #14110C 100%); }
      .settings__refs { display: flex; flex-direction: column; gap: var(--s-3); }
      .settings__ref-row { display: grid; grid-template-columns: auto 180px 1fr; gap: var(--s-3); align-items: center; padding: var(--s-2); }
      .danger-zone { border-color: var(--oxblood); border-left-width: 2px; }
      .danger-zone__title { color: var(--oxblood); }
      .danger-zone__action { display: grid; grid-template-columns: 1fr auto; gap: var(--s-5); align-items: center; padding: var(--s-4) 0; border-top: 1px dashed var(--line-soft); }
      .danger-zone__action:first-of-type { border-top: 0; }
      .danger-zone__action-title { font: var(--type-display-section); font-size: 18px; margin: 0 0 var(--s-2); color: var(--bone); }
      .danger-zone__action-body { color: var(--slate); font: var(--type-body-small); margin: 0; max-width: 540px; }
      .danger-zone__action-body strong { color: var(--oxblood); }
      .btn { display: inline-flex; align-items: center; padding: var(--s-3) var(--s-5); font: var(--type-label); letter-spacing: var(--tracking-label); text-transform: uppercase; }
      .btn--primary { background: var(--bronze); color: var(--text-on-ink); }
      .btn--primary:hover { background: var(--bronze-deep); }
      .btn--ghost { background: transparent; color: var(--bone); border: 1px solid var(--line); }
      .btn--ghost:hover { border-color: var(--bone); }
      .btn--destructive { background: var(--oxblood); color: var(--text-on-ink); }
      .btn--destructive:hover { background: var(--oxblood-bright); }
      @media (max-width: 768px) {
        .settings-row { grid-template-columns: 1fr; }
        .danger-zone__action { grid-template-columns: 1fr; }
      }
    `}</style>
  );
}
